import {
  signInAnonymously,
  onAuthStateChanged,
  GoogleAuthProvider,
  browserPopupRedirectResolver,
  linkWithPopup,
  signInWithCredential,
  signInWithPopup,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  deleteUser,
} from "firebase/auth";
import { auth } from "../firebase/config";

/** @returns {import('firebase/auth').Unsubscribe} */
export function subscribeAuthState(listener) {
  return onAuthStateChanged(auth, listener);
}

export function getCurrentAuthUser() {
  return auth.currentUser;
}

export async function signInAnonymousUser() {
  const credential = await signInAnonymously(auth);
  return credential.user;
}

export function createGoogleAuthProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

/**
 * Links Google to the current user, or signs into the existing Google account
 * when the credential is already linked (one popup — reuses credential on conflict).
 * @param {import('firebase/auth').User} user
 */
export async function linkOrSignInGoogleWithUser(user) {
  const provider = createGoogleAuthProvider();
  try {
    const credential = await linkWithPopup(user, provider, browserPopupRedirectResolver);
    return credential.user;
  } catch (error) {
    if (!isGoogleCredentialAlreadyInUse(error)) {
      throw error;
    }
    const oauthCredential = GoogleAuthProvider.credentialFromError(error);
    if (oauthCredential) {
      const signedIn = await signInWithCredential(auth, oauthCredential);
      return signedIn.user;
    }
    const signedIn = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
    return signedIn.user;
  }
}

/**
 * @deprecated Prefer linkOrSignInGoogleWithUser
 */
export async function linkGoogleProviderToUser(user) {
  return linkOrSignInGoogleWithUser(user);
}

/** @param {unknown} error */
export function isGoogleCredentialAlreadyInUse(error) {
  const code =
    error && typeof error === "object" && "code" in error
      ? String(error.code)
      : "";
  return (
    code === "auth/credential-already-in-use" ||
    code === "auth/email-already-in-use"
  );
}

export function userHasGoogleProvider(user) {
  return Boolean(
    user?.providerData?.some((p) => p.providerId === "google.com")
  );
}

export async function signOutCurrentUser() {
  await signOut(auth);
}

/**
 * Permanently deletes the Firebase Auth user (erase account mode).
 * @param {import('firebase/auth').User} [user]
 */
export async function deleteCurrentAuthUser(user = auth.currentUser) {
  if (!user) throw new Error("No signed-in user to delete");
  await deleteUser(user);
}

export async function signInWithGooglePopup() {
  const provider = createGoogleAuthProvider();
  return signInWithPopup(auth, provider, browserPopupRedirectResolver);
}

export async function signInWithEmailPassword(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function createUserWithEmailPassword(email, password) {
  return createUserWithEmailAndPassword(auth, email, password);
}

export function userHasPasswordProvider(user) {
  return Boolean(
    user?.providerData?.some((p) => p.providerId === "password")
  );
}
